import React, { FC } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@mui/material";

import DateInput from "@/components/atoms/Form/DateInput/DateInput";

import { formSchema } from "./formSchema";

export interface FormDateRange {
    dateFrom: Date;
    dateUntil: Date;
}

export const defaultValues = (): FormDateRange => ({
    dateFrom: null,
    dateUntil: null,
});

type DateRangeFormProps = {
    initialValues?: FormDateRange;
    onChange: (values: FormDateRange) => void;
};

const DateRangeForm: FC<DateRangeFormProps> = ({ initialValues, onChange }) => {
    const { t } = useTranslation();
    const methods = useForm<FormDateRange>({
        mode: "onBlur",
        defaultValues: initialValues || defaultValues(),
        resolver: yupResolver(formSchema()),
    });

    const { handleSubmit } = methods;

    const watchDateFrom = methods.watch("dateFrom");
    const watchDateUntil = methods.watch("dateUntil");

    const onSubmit = async (formValues: FormDateRange) => {
        const formValuesMapped = { ...formValues };
        if (!watchDateUntil) {
            formValuesMapped.dateUntil = new Date(watchDateFrom.getTime());
        }
        if (formValuesMapped.dateFrom) {
            formValuesMapped.dateFrom.setHours(0);
            formValuesMapped.dateFrom.setMinutes(0);
            formValuesMapped.dateFrom.setSeconds(0);
        }
        if (formValuesMapped.dateUntil) {
            formValuesMapped.dateUntil.setHours(23);
            formValuesMapped.dateUntil.setMinutes(59);
            formValuesMapped.dateUntil.setSeconds(59);
        }
        onChange(formValuesMapped);
    };
    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DateInput control={methods.control} name="dateFrom" disableFuture label={t("Common.From")} />
                <DateInput control={methods.control} name="dateUntil" disableFuture label={t("Common.To")} />
                <Button fullWidth onClick={methods.handleSubmit(onSubmit)} data-testid="filter_submit-button">
                    {t("Common.Apply")}
                </Button>
            </form>
        </FormProvider>
    );
};
export default DateRangeForm;
